import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

export interface Booking {
  id: string;
  client_id: string;
  booking_date: string;
  time_slot: string;
  workout_type_id: string;
  organization_id: string;
  status: 'confirmed' | 'completed' | 'missed' | 'cancelled';
  created_at: string;
  updated_at: string;
  client?: {
    id: string;
    firstName: string;
    lastName: string;
    phone: string;
    email?: string;
    paymentStatus?: string;
    subscriptions?: Array<{
      id: string;
      workoutTypeId: string;
      sessionsPerMonth: number;
      currentCycleSessionsUsed: number;
      isActive: boolean;
    }>;
  };
}

export const useBookings = (date: Date, timeSlotId?: string) => {
  const { currentOrganization } = useAuth();

  return useQuery({
    queryKey: ['bookings', currentOrganization?.id, format(date, 'yyyy-MM-dd'), timeSlotId],
    queryFn: async (): Promise<Booking[]> => {
      if (!currentOrganization) return [];

      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          clients!inner (
            id,
            first_name,
            last_name,
            phone,
            email,
            payment_status,
            client_subscriptions!inner (
              id,
              workout_type_id,
              sessions_per_month,
              current_cycle_sessions_used,
              is_active
            )
          )
        `)
        .eq('organization_id', currentOrganization.id)
        .eq('booking_date', format(date, 'yyyy-MM-dd'))
        .order('time_slot');

      if (error) {
        console.error('Error fetching bookings:', error);
        throw error;
      }

      return data?.map(booking => ({
        ...booking,
        status: booking.status as 'confirmed' | 'completed' | 'missed' | 'cancelled',
        client: {
          id: booking.clients.id,
          firstName: booking.clients.first_name,
          lastName: booking.clients.last_name,
          phone: booking.clients.phone,
          email: booking.clients.email,
          paymentStatus: booking.clients.payment_status,
          subscriptions: booking.clients.client_subscriptions?.map((sub: any) => ({
            id: sub.id,
            workoutTypeId: sub.workout_type_id,
            sessionsPerMonth: sub.sessions_per_month,
            currentCycleSessionsUsed: sub.current_cycle_sessions_used,
            isActive: sub.is_active,
          })) || []
        }
      })) || [];
    },
    enabled: !!currentOrganization?.id,
  });
};

export const useAddClientToBooking = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      clientId,
      timeSlotId,
      bookingDate,
      workoutTypeId,
    }: {
      clientId: string;
      timeSlotId: string;
      bookingDate: Date;
      workoutTypeId: string;
    }) => {
      const { data, error } = await supabase.rpc('add_client_to_booking', {
        p_client_id: clientId,
        p_time_slot_id: timeSlotId,
        p_booking_date: format(bookingDate, 'yyyy-MM-dd'),
        p_workout_type_id: workoutTypeId,
      });

      if (error) {
        console.error('Error adding client to booking:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['effective-bookings'] });
      toast.success(t('calendar.clientBookedSuccessfully'));
    },
    onError: (error: any) => {
      console.error('Add client to booking error:', error);
      // Check for specific error messages and translate them
      let errorMessage = t('calendar.bookingFailed');
      if (error.message?.includes('already booked') || error.message?.includes('duplicate')) {
        errorMessage = t('calendar.clientAlreadyBooked');
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    },
  });
};

export const useUpdateBookingStatus = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      bookingId,
      status,
      incrementSessions = true,
    }: {
      bookingId: string;
      status: string;
      incrementSessions?: boolean;
    }) => {
      const { data, error } = await supabase.rpc('update_booking_status', {
        p_booking_id: bookingId,
        p_status: status,
        p_increment_sessions: incrementSessions,
      });

      if (error) {
        console.error('Error updating booking status:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['effective-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      
      if (variables.status === 'completed') {
        toast.success(t('calendar.sessionMarkedCompleted'));
      } else if (variables.status === 'missed') {
        toast.success(t('calendar.sessionMarkedAbsent'));
      }
    },
    onError: (error: any) => {
      console.error('Update booking status error:', error);
      toast.error(error.message || t('calendar.updateBookingFailed'));
    },
  });
};

export const useRemoveClientFromBooking = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookingId: string) => {
      const { data, error } = await supabase.rpc('remove_client_from_booking', {
        p_booking_id: bookingId,
      });

      if (error) {
        console.error('Error removing client from booking:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['effective-bookings'] });
      toast.success(t('calendar.clientRemovedSuccessfully'));
    },
    onError: (error: any) => {
      console.error('Remove client from booking error:', error);
      toast.error(error.message || t('calendar.removeClientFailed'));
    },
  });
};
